import { ActionType, PlayerAction } from '../types/actions';
import { EnginePhase, MatchState, Seat, otherSeat } from '../types/state';
import { CardLibrary, findCard } from './helpers';
import { GameEvent } from '../types/events';
import {
  actionBias,
  actionCardId,
  evaluateState,
  exodiaStallBias,
  fusionFieldPenalty,
  isCombatSensitiveSpell,
} from './aiEval';
import { enumerateAiActions, tryApplyAction } from './aiActions';
import { combatOutlookScore, planNextAttack } from './aiCombat';
import { plannerDifficulty, type AiDeckDifficulty } from './aiDeck';
import { aiLowValueDiscard, planAiChainResponses } from './aiResponses';

/** Engine surface the AI stepper needs (avoids importing GameEngine here). */
export interface AiEngine {
  getState(): MatchState;
  getLibrary(): CardLibrary;
  hasPendingResponse(): boolean;
  applyAction(seat: Seat, action: PlayerAction): GameEvent[];
  getStateForSeat(viewer: Seat): MatchState;
  /** Both hands visible, decks hidden. Captured with each batch. */
  getStateForSpectator(): MatchState;
}

/**
 * One AI (or host) action plus the perspective-filtered board after it.
 * Callers must not re-read the live engine after `collectAISteps` — that
 * snapshot is already the end of the whole turn.
 */
export interface AiEventBatch {
  events: GameEvent[];
  states: [MatchState, MatchState];
  /**
   * Admin watch snapshot taken with `states`. Absent only on a live
   * re-broadcast of the current board (no new events).
   */
  watch?: MatchState;
}

/** Snapshot both seats after `events` were just applied. */
export function captureEventBatch(
  engine: AiEngine,
  events: GameEvent[],
): AiEventBatch | null {
  if (events.length === 0) return null;
  return {
    events,
    states: [engine.getStateForSeat(0), engine.getStateForSeat(1)],
    watch: engine.getStateForSpectator(),
  };
}

const ADVANCE: PlayerAction = { type: ActionType.ADVANCE_PHASE };

function searchBudget(level: AiDeckDifficulty): { beam: number; depth: number } {
  switch (level) {
    case 'test':
    case 'easy':
      return { beam: 3, depth: 3 };
    case 'expert':
      return { beam: 5, depth: 6 };
    case 'master':
      return { beam: 6, depth: 7 };
    default:
      return { beam: 4, depth: 5 };
  }
}

interface BeamNode {
  first: PlayerAction;
  state: MatchState;
  score: number;
  pathBias: number;
  leaf: boolean;
}

/**
 * Picks ONE action for the current state. The host applies it, emits it, then
 * asks again so the client can animate each play instead of jumping the turn.
 */
export function decideAction(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
): PlayerAction {
  if (state.turnPlayer !== seat || state.status !== 'active') return ADVANCE;

  switch (state.phase) {
    case EnginePhase.MAIN_1:
    case EnginePhase.MAIN_2:
      return planMainPhase(state, library, seat);
    case EnginePhase.BATTLE:
      return planBattle(state, library, seat);
    default:
      return ADVANCE;
  }
}

function planMainPhase(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
): PlayerAction {
  const { beam: width, depth } = searchBudget(plannerDifficulty(state, seat));
  let beam = expand(state, library, seat, null, 0, width);
  if (beam.length === 0) return ADVANCE;

  for (let d = 1; d < depth; d++) {
    const next: BeamNode[] = [];
    for (const node of beam) {
      if (node.leaf || !canExpand(node.state, seat)) {
        next.push(node);
        continue;
      }
      const children = expand(
        node.state, library, seat, node.first, node.pathBias, width,
      );
      if (children.length === 0) {
        next.push(node);
        continue;
      }
      next.push(...children);
    }
    next.sort((a, b) => b.score - a.score);
    beam = next.slice(0, width);
  }

  beam.sort((a, b) => b.score - a.score);
  return beam[0]?.first ?? ADVANCE;
}

function canExpand(state: MatchState, seat: Seat): boolean {
  if (state.status !== 'active') return false;
  if (state.turnPlayer !== seat) return false;
  if (
    state.pendingPriority ||
    state.pendingDiscard ||
    state.pendingFlipEffect ||
    state.pendingTargets ||
    state.pendingOfferingSummon !== null ||
    state.pendingCoinCall !== null ||
    state.pendingChoice !== null ||
    state.pendingChainOrder !== null ||
    state.chain.length > 0
  ) {
    return false;
  }
  return state.phase === EnginePhase.MAIN_1 || state.phase === EnginePhase.MAIN_2;
}

function expand(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  first: PlayerAction | null,
  pathBias: number,
  width: number,
): BeamNode[] {
  const nodes: BeamNode[] = [];
  for (const action of enumerateAiActions(state, library, seat)) {
    const scored = scoreAction(state, library, seat, action);
    if (scored === null) continue;
    const nodeFirst = first ?? action;
    const nextBias = pathBias + scored.bias;
    const score = scored.board + nextBias;
    const finished = scored.next.status === 'finished';
    if (action.type === ActionType.ADVANCE_PHASE) {
      nodes.push({
        first: nodeFirst, state, score, pathBias: nextBias, leaf: true,
      });
      continue;
    }
    nodes.push({
      first: nodeFirst,
      state: scored.next,
      score,
      pathBias: nextBias,
      leaf: finished,
    });
  }
  nodes.sort((a, b) => b.score - a.score);
  return nodes.slice(0, width);
}

function planBattle(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
): PlayerAction {
  return planNextAttack(state, library, seat);
}

function scoreAction(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  action: PlayerAction,
): { next: MatchState; board: number; bias: number } | null {
  if (action.type === ActionType.ADVANCE_PHASE) {
    return {
      next: state,
      board: evaluateState(state, library, seat),
      bias: exodiaStallBias(state, seat, action),
    };
  }
  const bias = actionBias(state, library, seat, action) + exodiaStallBias(state, seat, action);
  const next = tryApplyAction(state, library, seat, action);
  if (!next) return null;
  let extra = 0;
  const before = combatOutlookScore(state, library, seat);
  const after = combatOutlookScore(next, library, seat);
  if (action.type === ActionType.TRIBUTE_SUMMON && after <= before) {
    extra -= 420;
  }
  const spellId = actionCardId(state, seat, action);
  if (spellId === '24094653') {
    extra -= fusionFieldPenalty(state, next, library, seat);
  }
  if (spellId && isCombatSensitiveSpell(spellId) && after <= before) {
    extra -= 420;
  }
  return { next, board: evaluateState(next, library, seat), bias: bias + extra };
}

/**
 * Applies the AI's next legal action, or `null` if a human must act (or Standby
 * is holding). Illegal picks fall through to ADVANCE_PHASE so the turn cannot stall.
 *
 * A Flip Effect on the opponent's turn (Swords of Revealing Light flipping
 * Trap Master) parks pendingFlipEffect / pendingTargets on the AI seat.
 * Those windows used to freeze the vs-AI host: hasPendingResponse was true
 * and turnPlayer was still the human, so this stepper never answered.
 */
export function stepAI(engine: AiEngine): GameEvent[] | null {
  const state = engine.getState();
  if (state.status !== 'active') return null;
  const pending = decidePendingAiAction(state, engine.getLibrary());
  if (pending) {
    try {
      return engine.applyAction(pending.seat, pending.action);
    } catch {
      const window = engine.getState().pendingPriority;
      if (!window || window.responder !== pending.seat) return null;
      try {
        return engine.applyAction(pending.seat, {
          type: ActionType.RESPOND_PRIORITY,
          requestId: window.requestId,
          cardUid: null,
        });
      } catch {
        return null;
      }
    }
  }
  if (engine.hasPendingResponse()) return null;
  const seat = state.turnPlayer;
  if (!state.seats[seat].isAI) return null;
  // Standby is shown for STANDBY_HOLD_MS; the host timer advances it.
  if (state.phase === EnginePhase.STANDBY) return null;
  const action = decideAction(state, engine.getLibrary(), seat);
  try {
    return engine.applyAction(seat, action);
  } catch {
    return engine.applyAction(seat, ADVANCE);
  }
}

/**
 * One action that closes an engine window owed by an AI seat. Null when the
 * window belongs to a human, or when nobody owes one.
 */
function decidePendingAiAction(
  state: MatchState,
  library: CardLibrary,
): { seat: Seat; action: PlayerAction } | null {
  const flip = state.pendingFlipEffect;
  if (flip) {
    if (!state.seats[flip.chooser].isAI) return null;
    const targetUid = pickAiWindowUid(state, flip.chooser, flip.targetUids);
    if (!targetUid) return null;
    return {
      seat: flip.chooser,
      action: {
        type: ActionType.RESOLVE_FLIP_TARGET,
        requestId: flip.requestId,
        targetUid,
      },
    };
  }
  const targets = state.pendingTargets;
  if (targets) {
    if (!state.seats[targets.chooser].isAI) return null;
    const pick = pickAiTargetWindow(state, targets.chooser, targets);
    return {
      seat: targets.chooser,
      action: {
        type: ActionType.SELECT_TARGETS,
        requestId: targets.requestId,
        targetUids: pick,
      },
    };
  }
  const priority = state.pendingPriority;
  if (priority) {
    if (!state.seats[priority.responder].isAI) return null;
    const planned = planAiChainResponses(
      state, library, priority.responder, priority.activatableUids,
    )[0];
    return {
      seat: priority.responder,
      action: {
        type: ActionType.RESPOND_PRIORITY,
        requestId: priority.requestId,
        cardUid: planned?.uid ?? null,
        ...(planned?.targets ? { targetUids: planned.targets } : {}),
      },
    };
  }
  const discard = state.pendingDiscard;
  if (discard) {
    if (!state.seats[discard.seat].isAI) return null;
    const cardUids: string[] = [];
    const taken = new Set<string>();
    const clone: MatchState = {
      ...state,
      zones: [
        { ...state.zones[0], hand: [...state.zones[0].hand] },
        { ...state.zones[1], hand: [...state.zones[1].hand] },
      ],
    };
    for (let n = 0; n < discard.count; n++) {
      const uid = aiLowValueDiscard(clone, library, discard.seat);
      if (!uid || taken.has(uid)) break;
      taken.add(uid);
      cardUids.push(uid);
      clone.zones[discard.seat].hand = clone.zones[discard.seat].hand.filter(
        (card) => card.uid !== uid,
      );
    }
    if (cardUids.length < discard.count) return null;
    return {
      seat: discard.seat,
      action: { type: ActionType.DISCARD, cardUids },
    };
  }
  const coin = state.pendingCoinCall;
  if (coin) {
    if (!state.seats[coin.chooser].isAI) return null;
    return {
      seat: coin.chooser,
      action: { type: ActionType.CALL_COIN, requestId: coin.requestId, call: 'heads' },
    };
  }
  const choice = state.pendingChoice;
  if (choice) {
    if (!state.seats[choice.chooser].isAI) return null;
    return {
      seat: choice.chooser,
      action: {
        type: ActionType.ANSWER_CHOICE,
        requestId: choice.requestId,
        accept: false,
      },
    };
  }
  const order = state.pendingChainOrder;
  if (order) {
    if (!state.seats[order.chooser].isAI) return null;
    return {
      seat: order.chooser,
      action: {
        type: ActionType.ORDER_CHAIN,
        requestId: order.requestId,
        cardUids: order.cards.map((card) => card.uid),
      },
    };
  }
  if (state.pendingOfferingSummon !== null) {
    const seat = state.pendingOfferingSummon;
    if (!state.seats[seat].isAI) return null;
    return { seat, action: ADVANCE };
  }
  return null;
}

/** Opponent's cards first, then any listed uid. Never leaves the window empty. */
function pickAiWindowUid(state: MatchState, seat: Seat, uids: string[]): string | undefined {
  const opp = otherSeat(seat);
  return (
    uids.find((uid) => findCard(state, uid)?.location.seat === opp) ??
    uids[0]
  );
}

function pickAiTargetWindow(
  state: MatchState,
  seat: Seat,
  pending: NonNullable<MatchState['pendingTargets']>,
): string[] {
  if (pending.reorderDeckTop) return [...pending.candidateUids];
  const min = Math.max(pending.min, 0);
  if (min === 0) return [];
  const preferred: string[] = [];
  const rest: string[] = [];
  const opp = otherSeat(seat);
  for (const uid of pending.candidateUids) {
    if (findCard(state, uid)?.location.seat === opp) preferred.push(uid);
    else rest.push(uid);
  }
  return [...preferred, ...rest].slice(0, Math.min(min, pending.max));
}

/**
 * One batch per AI action, each with the board as it was right after that
 * action. The live engine is at the last batch when this returns.
 */
export function collectAISteps(engine: AiEngine): AiEventBatch[] {
  const batches: AiEventBatch[] = [];
  let guard = 0;
  while (guard < 500) {
    const events = stepAI(engine);
    if (!events || events.length === 0) break;
    const batch = captureEventBatch(engine, events);
    if (batch) batches.push(batch);
    guard += 1;
  }
  return batches;
}
